import { Component, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { TranslateModule } from '@ngx-translate/core';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TransactionService } from '../../../shared/services/transaction.service';
import { NotificationDetailDto } from '../../../models/transaction-detail.model';
import { ApproveStatus } from '../../../models/transaction.model';
import { ApproveItemStatus, TransactionItem } from '../../../models/item.model';
import { EnvironmentService } from '../../../shared/services/environment.service';
import { MockTransactionData } from '../../../mocks/mock-transaction-data';
import { BehaviorSubject, lastValueFrom, of, catchError, finalize, Subscription } from 'rxjs';
import { NotificationService } from '../../../shared/services/notification.service';
import { TranslateService } from '@ngx-translate/core';

interface TransactionDetailViewModel {
  notification: NotificationDetailDto;
  items: TransactionItem[];
  readonly: boolean;
  canFinalize: boolean;
}

@Component({
  selector: 'app-transaction-detail',
  standalone: true,
  templateUrl: './transactionDetail.component.html',
  styleUrls: ['./transactionDetail.component.less'],
  imports: [
    CommonModule,
    HttpClientModule,
    TranslateModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    MatSnackBarModule
  ]
})
export class TransactionDetailComponent implements OnInit, OnDestroy {
  private _route = inject(ActivatedRoute);
  private _txService = inject(TransactionService);
  private _router = inject(Router);
  private _environment = inject(EnvironmentService);
  private _notifService = inject(NotificationService);
  private _translate = inject(TranslateService);
  private _snackBar = inject(MatSnackBar);

  public processing = signal(false);
  public loading = signal(true);
  public ApproveStatus = ApproveStatus;
  public ApproveItemStatus = ApproveItemStatus;

  public viewModel$ = new BehaviorSubject<TransactionDetailViewModel | null>(null);
  public errorMessage = '';
  public parsedNotes: Record<number, { articleCode?: string; ean?: string; name?: string; discountType?: string }> = {};

  private _paramSub?: Subscription;

  ngOnInit(): void {
    this._paramSub = this._route.paramMap.subscribe(() => {
      this.loadTransactionData();
    });
  }

  ngOnDestroy(): void {
    this._paramSub?.unsubscribe();
  }
  private async loadTransactionData(): Promise<void> {
    const idParam = this._route.snapshot.paramMap.get('id');

    if (!idParam) {
      this.errorMessage = 'No transaction ID provided';
      this.loading.set(false);
      return;
    }

    const txId = Number(idParam);
    if (isNaN(txId)) {
      this.errorMessage = 'Invalid transaction ID.';
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    this.errorMessage = '';
    this.viewModel$.next(null);
    console.log('[TransactionDetail] Loading transaction data for ID:', txId);

    await this._notifService.markNotificationAsRead(idParam);

    const source$ = this._environment.isDesignMode
      ? of(MockTransactionData.getTransactionDetail(txId))
      : this._txService.getTransactionDetail(txId);

    const transaction$ = source$.pipe(
      catchError(error => {
        console.error('[TransactionDetail] Error loading transaction:', error);
        this.errorMessage = 'Failed to load transaction. Please try again.';
        return of(null);
      }),
      finalize(() => {
        this.loading.set(false);
      })
    );

    const notification = await lastValueFrom(transaction$);

    if (!notification) {
      this.errorMessage = 'Transaction not found.';
      return;
    }

    const items = this.getDisplayItems(notification);
    this.viewModel$.next(this.createViewModel(notification, items));
    this.buildParsedNotes(items);
  }

  setItemStatus(item: TransactionItem, status: ApproveItemStatus) {
    const viewModel = this.viewModel$.value;
    if (!viewModel || viewModel.readonly) return;

    const updatedItems = viewModel.items.map(i =>
      i.foreignItemID === item.foreignItemID ? { ...i, status } : i
    );

    this.viewModel$.next(this.createViewModel(viewModel.notification, updatedItems));
  }

  async finalizeTransaction(): Promise<void> {
    const viewModel = this.viewModel$.value;
    if (!viewModel || viewModel.readonly || !viewModel.canFinalize) return;

    this.processing.set(true);
    this.errorMessage = '';

    if (this._environment.isDesignMode) {
      MockTransactionData.updateItemStatuses(viewModel.notification.NotificationID, viewModel.items);
      this.processing.set(false);
      setTimeout(() => {
        this._router.navigate(['/transaction']).then(() => window.scrollTo({ top: 0, left: 0 }));
      }, 500);
      return;
    }

    console.log('Sending update for transaction:', viewModel.notification.NotificationID, 'with items:', viewModel.items);

    const result = await lastValueFrom(
      this._txService.updateItems(viewModel.notification.NotificationID, viewModel.items).pipe(
        catchError(err => {
          console.error('Finalize failed with error:', err);
          const resultText: string | undefined = err.error?.resultText;
          const message = resultText
            ? this._translate.instant(resultText)
            : `Failed to finalize transaction: ${err.status || 'Unknown error'}`;
          this._snackBar.open(message, this._translate.instant('GLOBAL.CLOSE'), { duration: 5000 });
          this.processing.set(false);
          return of(null);
        })
      )
    );

    this.processing.set(false);

    if (result === null) {
      console.log('Error case - reloading transaction data');
      await this.loadTransactionData();
    } else {
      console.log('Success case - update completed, result:', result);
      this._router.navigate(['/transaction']).then(() => window.scrollTo({ top: 0, left: 0 }));
    }
  }
  public hasPendingItems(): boolean {
    return !this.viewModel$.value?.canFinalize;
  }

  public splitLines(text: string): string[] {
    if (!text) return [];
    return text.split(/\/n|\n/).map(l => l.trim()).filter(l => l.length > 0);
  }

  private buildParsedNotes(items: TransactionItem[]): void {
    this.parsedNotes = {};
    for (const item of items) {
      this.parsedNotes[item.foreignItemID] = this.parseNote(item.requestNote);
    }
  }

  private getDisplayItems(notification: NotificationDetailDto): TransactionItem[] {
    return (notification.Transaction.DocumentUpdated ?? notification.Transaction.Document).map(item => ({ ...item }));
  }

  private createViewModel(notification: NotificationDetailDto, items: TransactionItem[]): TransactionDetailViewModel {
    const readonly = this.isTerminalStatus(notification.Transaction.Status);

    return {
      notification,
      items,
      readonly,
      canFinalize: !readonly && this.areAllItemsDecided(items)
    };
  }

  private isTerminalStatus(status: ApproveStatus): boolean {
    return status === ApproveStatus.Finished
      || status === ApproveStatus.Timeouted
      || status === ApproveStatus.Cancelled;
  }

  private areAllItemsDecided(items: TransactionItem[]): boolean {
    return items.length > 0 && items.every(item =>
      item.status === ApproveItemStatus.Approved || item.status === ApproveItemStatus.Declined
    );
  }

  private parseNote(note: string | undefined): { articleCode?: string; ean?: string; name?: string; discountType?: string } {
    if (!note) return {};
    const articleMatch = note.match(/[Čč][ií]slo artiklu:\s*(\S+)/i);
    const eanMatch = note.match(/EAN:\s*(\S+)/i);
    const nameMatch = note.match(/Jm[eé]no:\s*(.+?)(?:\s*-\s*Sleva:|$)/i);
    const discountMatch = note.match(/Sleva:\s*(.+)$/i);
    if (!articleMatch && !eanMatch && !nameMatch) return { name: note };
    return {
      articleCode: articleMatch?.[1]?.replace(/,\s*$/, ''),
      ean: eanMatch?.[1]?.replace(/,\s*$/, ''),
      name: nameMatch?.[1]?.trim(),
      discountType: discountMatch?.[1]?.trim()
    };
  }
}
